import React from 'react';
import { Box, Typography, Icon } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import clsx from 'clsx';

// Mapa de estados -> etiqueta legible + clase de color de utilidad del DS.
// Valores confirmados por el backend (talisis-brightspace-api):
//   synced | missing_enrollment | missing_mapping | missing_crn | no_d2l_account
const STATUS_META = {
    synced: { label: 'Inscrito', color: 'text-success-300', dot: 'success' },
    missing_enrollment: { label: 'Falta inscripción', color: 'text-warning-300', dot: 'warning' },
    missing_mapping: { label: 'Sin mapear (aula existe)', color: 'text-warning-300', dot: 'warning' },
    missing_crn: { label: 'Aula inexistente — reportar a TI', color: 'text-error-300', dot: 'error' },
    no_d2l_account: { label: 'Sin cuenta D2L', color: 'text-error-300', dot: 'error' },
};

// Mapa de tool -> etiqueta legible + icono para el encabezado de la tarjeta.
const TOOL_META = {
    consultar_sync: { label: 'Consulta de sincronización', icon: 'ri-search-eye-line' },
    sync_student: { label: 'Sincronización de alumno', icon: 'ri-user-follow-line' },
    consultar_crn: { label: 'Consulta de CRN', icon: 'ri-search-eye-line' },
    sync_crn: { label: 'Sincronización de CRN', icon: 'ri-group-line' },
    resolve_duplicate: { label: 'Resolución de aula duplicada', icon: 'ri-git-merge-line' },
    map_crn: { label: 'Mapeo de CRN', icon: 'ri-links-line' },
    mapear_crn_asistido: { label: 'Mapeo asistido de CRN', icon: 'ri-links-line' },
};

const useStyles = makeStyles((theme) => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        width: '100%',
    },
    toolHeader: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        paddingBottom: '8px',
        borderBottom: '1px solid ' + theme.palette.colors.fillStroke[200],
    },
    toolHeaderLabel: {
        color: theme.palette.white,
        fontSize: '13px',
        fontWeight: 700,
        letterSpacing: '0.2px',
    },
    syncBannerOk: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: '8px',
        padding: '12px',
        borderRadius: '8px',
        backgroundColor: 'rgba(1, 237, 140, 0.08)',
        border: '1px solid ' + theme.palette.colors.success[300],
    },
    syncBannerWarn: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: '8px',
        padding: '12px',
        borderRadius: '8px',
        backgroundColor: 'rgba(252, 220, 67, 0.08)',
        border: '1px solid ' + theme.palette.colors.warning[300],
    },
    syncBannerText: {
        color: theme.palette.colors.gray[100],
        fontSize: '13px',
        lineHeight: 1.5,
        fontWeight: 500,
    },
    crnStatusRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '12px',
        padding: '12px',
        borderRadius: '8px',
        backgroundColor: theme.palette.colors.fillStroke[400],
        border: '1px solid ' + theme.palette.colors.fillStroke[200],
    },
    crnStatusLabel: {
        color: theme.palette.colors.darkBlack[100],
        fontSize: '12px',
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
    },
    personSection: {
        display: 'flex',
        flexDirection: 'column',
        gap: '6px',
    },
    personSectionLabel: {
        color: theme.palette.white,
        fontSize: '12px',
        fontWeight: 700,
        letterSpacing: '0.3px',
    },
    d2lCard: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '12px',
        padding: '12px',
        borderRadius: '8px',
        backgroundColor: theme.palette.colors.fillStroke[400],
        border: '1px solid ' + theme.palette.colors.fillStroke[200],
    },
    d2lMain: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        minWidth: 0,
    },
    sectionHead: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
    },
    sectionHeadLabel: {
        color: theme.palette.colors.success[300],
        fontSize: '12px',
        fontWeight: 600,
        letterSpacing: '0.3px',
    },
    infoGrid: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '8px',
    },
    infoChip: {
        display: 'flex',
        flexDirection: 'column',
        gap: '2px',
        padding: '8px 12px',
        borderRadius: '8px',
        backgroundColor: theme.palette.colors.fillStroke[400],
        border: '1px solid ' + theme.palette.colors.fillStroke[200],
        minWidth: '96px',
    },
    infoChipLabel: {
        color: theme.palette.colors.darkBlack[100],
        fontSize: '10px',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
    },
    infoChipValue: {
        color: theme.palette.white,
        fontSize: '14px',
        fontWeight: 600,
    },
    list: {
        display: 'flex',
        flexDirection: 'column',
        gap: '6px',
    },
    row: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '12px',
        padding: '10px 12px',
        borderRadius: '8px',
        backgroundColor: theme.palette.colors.fillStroke[400],
        border: '1px solid ' + theme.palette.colors.fillStroke[200],
    },
    rowMain: {
        display: 'flex',
        flexDirection: 'column',
        gap: '2px',
        minWidth: 0,
    },
    rowPrimary: {
        color: theme.palette.white,
        fontSize: '13px',
        fontWeight: 600,
    },
    rowSecondary: {
        color: theme.palette.colors.darkBlack[100],
        fontSize: '11px',
    },
    badge: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        flexShrink: 0,
        padding: '4px 10px',
        borderRadius: '999px',
        backgroundColor: theme.palette.colors.fillStroke[300],
        border: '1px solid ' + theme.palette.colors.fillStroke[200],
        fontSize: '11px',
        fontWeight: 600,
        whiteSpace: 'nowrap',
    },
    badgeDot: {
        width: '6px',
        height: '6px',
        borderRadius: '50%',
    },
    dotSuccess: { backgroundColor: theme.palette.colors.success[300] },
    dotWarning: { backgroundColor: theme.palette.colors.warning[300] },
    dotError: { backgroundColor: theme.palette.colors.error[300] },
    dotMuted: { backgroundColor: theme.palette.colors.darkBlack[100] },
    actionsBox: {
        display: 'flex',
        flexDirection: 'column',
        gap: '6px',
        padding: '12px',
        borderRadius: '8px',
        backgroundColor: 'rgba(1, 237, 140, 0.08)',
        border: '1px solid ' + theme.palette.colors.success[300],
    },
    errorBox: {
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
        padding: '12px',
        borderRadius: '8px',
        backgroundColor: 'rgba(255, 92, 92, 0.08)',
        border: '1px solid ' + theme.palette.colors.error[300],
    },
    errorTitle: {
        color: theme.palette.colors.error[300],
        fontSize: '13px',
        fontWeight: 700,
    },
    errorDetail: {
        color: theme.palette.colors.gray[100],
        fontSize: '12px',
    },
    codeBlock: {
        backgroundColor: theme.palette.colors.darkBlack[400],
        border: '1px solid ' + theme.palette.colors.fillStroke[200],
        borderRadius: '10px',
        padding: '14px',
        margin: 0,
        overflowX: 'auto',
        fontFamily: "'JetBrains Mono', 'Courier New', monospace",
        fontSize: '12.5px',
        lineHeight: 1.6,
        color: theme.palette.colors.gray[200],
        whiteSpace: 'pre',
    },
}));

const StatusBadge = ({ status }) => {
    const classes = useStyles();
    const meta = STATUS_META[status];
    if (!meta) {
        return <Box className={classes.badge}>{status}</Box>;
    }
    const dotClass = {
        success: classes.dotSuccess,
        warning: classes.dotWarning,
        error: classes.dotError,
        muted: classes.dotMuted,
    }[meta.dot];
    return (
        <Box className={classes.badge}>
            <span className={clsx(classes.badgeDot, dotClass)} />
            <span className={meta.color}>{meta.label}</span>
        </Box>
    );
};

const InfoChip = ({ label, value }) => {
    const classes = useStyles();
    if (value === undefined || value === null || value === '') return null;
    return (
        <Box className={classes.infoChip}>
            <Typography className={classes.infoChipLabel}>{label}</Typography>
            <Typography className={classes.infoChipValue}>{value}</Typography>
        </Box>
    );
};

const JsonFallback = ({ data }) => {
    const classes = useStyles();
    return <pre className={classes.codeBlock}>{JSON.stringify(data, null, 2)}</pre>;
};

const ErrorBlock = ({ data }) => {
    const classes = useStyles();
    const title = data.error || data.status || 'Error';
    return (
        <Box className={classes.errorBox}>
            <Box className={classes.sectionHead}>
                <Icon className="ri-error-warning-line font-size-16 text-error-300" />
                <Typography className={classes.errorTitle}>{title}</Typography>
            </Box>
            {data.message && <Typography className={classes.errorDetail}>{data.message}</Typography>}
            {data.detail && <Typography className={classes.errorDetail}>{data.detail}</Typography>}
            {(data.term_code || data.crn || data.vpdi) && (
                <Typography className={classes.errorDetail}>
                    {[data.vpdi, data.term_code, data.crn && `CRN ${data.crn}`].filter(Boolean).join(' · ')}
                </Typography>
            )}
        </Box>
    );
};

const ActionsTaken = ({ actions, labelKey, title = 'Inscripciones realizadas' }) => {
    const classes = useStyles();
    if (!actions || actions.length === 0) return null;
    return (
        <Box className={classes.actionsBox}>
            <Box className={classes.sectionHead}>
                <Icon className="ri-checkbox-circle-line font-size-16 text-success-300" />
                <Typography className={classes.sectionHeadLabel}>
                    {title} ({actions.length})
                </Typography>
            </Box>
            <Box className={classes.list}>
                {actions.map((a, i) => (
                    <Typography key={i} className={classes.rowSecondary}>
                        {labelKey === 'crn' ? `CRN ${a.crn}` : a.matricula}
                        {a.org_unit_id ? ` · aula ${a.org_unit_id}` : ''}
                    </Typography>
                ))}
            </Box>
        </Box>
    );
};

// Detecta si data representa un error en vez del shape normal.
const isErrorData = (data) => !!data && data.error !== undefined;

// Banner con el mensaje redactado por el backend tras sincronizar (sync_student/sync_crn).
// data.message viene listo para mostrar; data.summary.enrolled decide el tono (verde/ámbar).
const SyncMessageBanner = ({ data }) => {
    const classes = useStyles();
    if (!data.message) return null;
    const enrolled = data.summary && typeof data.summary.enrolled === 'number' ? data.summary.enrolled : null;
    const positive = enrolled !== null ? enrolled > 0 : (Array.isArray(data.actions_taken) && data.actions_taken.length > 0);
    return (
        <Box className={positive ? classes.syncBannerOk : classes.syncBannerWarn}>
            <Icon className={clsx(
                positive ? 'ri-checkbox-circle-line text-success-300' : 'ri-information-line text-warning-300',
                'font-size-18',
            )} />
            <Typography className={classes.syncBannerText}>{data.message}</Typography>
        </Box>
    );
};

// Lista de personas (alumnos o docentes) con título y badges de status.
// students[] e instructors[] comparten la forma {matricula, user_id?, status}.
const PersonList = ({ title, people, icon }) => {
    const classes = useStyles();
    if (!Array.isArray(people) || people.length === 0) return null;
    return (
        <Box className={classes.personSection}>
            <Box className={classes.sectionHead}>
                {icon && <Icon className={clsx(icon, 'font-size-16 text-violet-300')} />}
                <Typography className={classes.personSectionLabel}>{title} ({people.length})</Typography>
            </Box>
            <Box className={classes.list}>
                {people.map((p, i) => (
                    <Box key={`${p.matricula}-${i}`} className={classes.row}>
                        <Box className={classes.rowMain}>
                            <Typography className={classes.rowPrimary}>{p.matricula}</Typography>
                            {p.user_id && (
                                <Typography className={classes.rowSecondary}>Usuario D2L {p.user_id}</Typography>
                            )}
                        </Box>
                        <StatusBadge status={p.status} />
                    </Box>
                ))}
            </Box>
        </Box>
    );
};

// Lista de materias (crns[] o imparte[]) con título y badges de status.
// Ambos bloques comparten la forma {crn, materia, status, org_unit_id?, user_id?}.
const CourseList = ({ title, courses, icon }) => {
    const classes = useStyles();
    if (!Array.isArray(courses) || courses.length === 0) return null;
    return (
        <Box className={classes.personSection}>
            <Box className={classes.sectionHead}>
                {icon && <Icon className={clsx(icon, 'font-size-16 text-violet-300')} />}
                <Typography className={classes.personSectionLabel}>{title} ({courses.length})</Typography>
            </Box>
            <Box className={classes.list}>
                {courses.map((c, i) => (
                    <Box key={`${c.crn}-${i}`} className={classes.row}>
                        <Box className={classes.rowMain}>
                            <Typography className={classes.rowPrimary}>{c.materia || c.crn}</Typography>
                            <Typography className={classes.rowSecondary}>
                                CRN {c.crn}{c.org_unit_id ? ` · aula ${c.org_unit_id}` : ''}
                            </Typography>
                        </Box>
                        <StatusBadge status={c.status} />
                    </Box>
                ))}
            </Box>
        </Box>
    );
};

// Tarjeta de estado de la cuenta D2L de la persona (bloque d2l de consultar_sync).
// d2l = { existe, user_id, activo, nombre }.
const D2lAccountCard = ({ d2l }) => {
    const classes = useStyles();
    if (!d2l) return null;
    let meta;
    if (!d2l.existe) {
        meta = { label: 'Sin cuenta D2L', color: 'text-error-300', dot: classes.dotError, icon: 'ri-user-unfollow-line' };
    } else if (d2l.activo === true) {
        meta = { label: 'Cuenta activa', color: 'text-success-300', dot: classes.dotSuccess, icon: 'ri-user-follow-line' };
    } else if (d2l.activo === false) {
        meta = { label: 'Cuenta desactivada', color: 'text-error-300', dot: classes.dotError, icon: 'ri-forbid-2-line' };
    } else {
        meta = { label: 'Estado desconocido', color: 'text-dark-black-100', dot: classes.dotMuted, icon: 'ri-question-line' };
    }
    return (
        <Box className={classes.d2lCard}>
            <Box className={classes.d2lMain}>
                <Icon className={clsx(meta.icon, 'font-size-20', meta.color)} />
                <Box className={classes.rowMain}>
                    <Typography className={classes.rowPrimary}>{d2l.nombre || 'Cuenta D2L'}</Typography>
                    {d2l.user_id && (
                        <Typography className={classes.rowSecondary}>Usuario D2L {d2l.user_id}</Typography>
                    )}
                </Box>
            </Box>
            <Box className={classes.badge}>
                <span className={clsx(classes.badgeDot, meta.dot)} />
                <span className={meta.color}>{meta.label}</span>
            </Box>
        </Box>
    );
};

const StudentSyncView = ({ data }) => {
    const classes = useStyles();
    const crns = Array.isArray(data.crns) ? data.crns : [];
    const imparte = Array.isArray(data.imparte) ? data.imparte : [];
    return (
        <Box className={classes.root}>
            <SyncMessageBanner data={data} />
            <Box className={classes.infoGrid}>
                <InfoChip label="Matrícula" value={data.matricula} />
                <InfoChip label="Periodo" value={data.periodo} />
                <InfoChip label="Institución" value={data.vpdi} />
            </Box>
            <D2lAccountCard d2l={data.d2l} />
            <CourseList title="Materias inscritas" courses={crns} icon="ri-book-open-line" />
            <CourseList title="Materias que imparte" courses={imparte} icon="ri-slideshow-line" />
            <ActionsTaken actions={data.actions_taken} labelKey="crn" />
        </Box>
    );
};

const CrnSyncView = ({ data }) => {
    const classes = useStyles();
    const students = Array.isArray(data.students) ? data.students : [];
    const hasDuplicate = Array.isArray(data.duplicate) && data.duplicate.length > 0;
    // Shape A: el CRN trae un status global (sin lista de alumnos).
    const hasGlobalStatus = !!data.status && students.length === 0;
    return (
        <Box className={classes.root}>
            <SyncMessageBanner data={data} />
            <Box className={classes.infoGrid}>
                <InfoChip label="CRN" value={data.crn} />
                <InfoChip label="Periodo" value={data.term_code} />
                <InfoChip label="Institución" value={data.vpdi} />
                <InfoChip label="Aula" value={data.org_unit_id} />
            </Box>
            {hasGlobalStatus && (
                <Box className={classes.crnStatusRow}>
                    <Typography className={classes.crnStatusLabel}>Estado del CRN</Typography>
                    <StatusBadge status={data.status} />
                </Box>
            )}
            {hasDuplicate && (
                <Box className={classes.errorBox}>
                    <Box className={classes.sectionHead}>
                        <Icon className="ri-error-warning-line font-size-16 text-warning-300" />
                        <Typography className={classes.errorTitle} style={{ color: undefined }}>
                            Aula duplicada — se requiere elegir el aula ganadora
                        </Typography>
                    </Box>
                    {data.duplicate.map((aula, i) => (
                        <Typography key={i} className={classes.errorDetail}>
                            {aula.name} · aula {aula.org_unit_id}
                            {aula.is_active ? ' · activa' : ''}
                            {aula.is_current_mapping ? ' · mapeo actual' : ''}
                        </Typography>
                    ))}
                </Box>
            )}
            <PersonList title="Alumnos" people={students} icon="ri-user-line" />
            <PersonList title="Docentes" people={data.instructors} icon="ri-user-star-line" />
            <ActionsTaken actions={data.actions_taken} labelKey="matricula" title="Alumnos inscritos" />
            <ActionsTaken actions={data.instructor_actions_taken} labelKey="matricula" title="Docentes inscritos" />
        </Box>
    );
};

const ResolveDuplicateView = ({ data }) => {
    const classes = useStyles();
    const moved = Array.isArray(data.enrollments_moved) ? data.enrollments_moved.length : 0;
    return (
        <Box className={classes.root}>
            <Box className={classes.infoGrid}>
                <InfoChip label="CRN" value={data.crn} />
                <InfoChip label="Periodo" value={data.term_code} />
                <InfoChip label="Aula ganadora" value={data.winner_org_unit_id} />
                <InfoChip label="Aula perdedora" value={data.loser_org_unit_id} />
            </Box>
            <Box className={classes.row}>
                <Box className={classes.rowMain}>
                    <Typography className={classes.rowPrimary}>Aula perdedora renombrada</Typography>
                    <Typography className={classes.rowSecondary}>{data.loser_renamed_to}</Typography>
                </Box>
            </Box>
            <Box className={classes.actionsBox}>
                <Box className={classes.sectionHead}>
                    <Icon className="ri-exchange-line font-size-16 text-success-300" />
                    <Typography className={classes.sectionHeadLabel}>
                        Inscripciones movidas: {moved}
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
};

const MapCrnView = ({ data }) => {
    const classes = useStyles();
    const aulas = Array.isArray(data.aulas) ? data.aulas : [];
    // Tono del banner según la acción del mapeo asistido.
    const bannerClass = {
        already_mapped: classes.syncBannerOk,
        duplicate: classes.syncBannerWarn,
        needs_course_creation: classes.errorBox,
    }[data.action] || classes.syncBannerWarn;
    const bannerIcon = {
        already_mapped: 'ri-checkbox-circle-line text-success-300',
        duplicate: 'ri-error-warning-line text-warning-300',
        needs_course_creation: 'ri-error-warning-line text-error-300',
    }[data.action] || 'ri-information-line text-warning-300';
    return (
        <Box className={classes.root}>
            {data.message && (
                <Box className={bannerClass}>
                    <Icon className={clsx(bannerIcon, 'font-size-18')} />
                    <Typography className={classes.syncBannerText}>{data.message}</Typography>
                </Box>
            )}
            <Box className={classes.infoGrid}>
                <InfoChip label="CRN" value={data.crn} />
                <InfoChip label="Periodo" value={data.term_code} />
                <InfoChip label="Institución" value={data.vpdi} />
                <InfoChip label="Aula" value={data.org_unit_id} />
            </Box>
            {aulas.length > 0 && (
                <Box className={classes.list}>
                    {aulas.map((aula, i) => (
                        <Box key={`${aula.org_unit_id}-${i}`} className={classes.row}>
                            <Box className={classes.rowMain}>
                                <Typography className={classes.rowPrimary}>{aula.name}</Typography>
                                <Typography className={classes.rowSecondary}>
                                    aula {aula.org_unit_id}{aula.code ? ` · ${aula.code}` : ''}
                                </Typography>
                            </Box>
                        </Box>
                    ))}
                </Box>
            )}
        </Box>
    );
};

const ResultRenderer = ({ tool, result }) => {
    const classes = useStyles();
    // result = { ok, data, actor } normalmente; a veces el error viene en result directo.
    const data = (result && result.data) || result || {};
    const errored = isErrorData(data);
    const meta = TOOL_META[tool];

    const renderBody = () => {
        if (errored) {
            return <ErrorBlock data={data} />;
        }
        switch (tool) {
            case 'consultar_sync':
            case 'sync_student':
                return <StudentSyncView data={data} />;
            case 'consultar_crn':
            case 'sync_crn':
                return <CrnSyncView data={data} />;
            case 'resolve_duplicate':
                return <ResolveDuplicateView data={data} />;
            case 'mapear_crn_asistido':
                return <MapCrnView data={data} />;
            default:
                // map_crn y cualquier forma no mapeada -> JSON legible
                return <JsonFallback data={data} />;
        }
    };

    return (
        <Box className={classes.root}>
            <Box className={classes.toolHeader}>
                <Icon className={clsx(meta ? meta.icon : 'ri-terminal-box-line', 'font-size-16', errored ? 'text-error-300' : 'text-violet-300')} />
                <Typography className={classes.toolHeaderLabel}>
                    {meta ? meta.label : (tool || 'Resultado')}
                </Typography>
            </Box>
            {renderBody()}
        </Box>
    );
};

export default ResultRenderer;
